"use client";

import { compressImageIfNeeded } from '@/lib/utils/imageCompression';
import { Instagram, CalendarClock, Image as ImageIcon, ImagePlus, Trash2 } from 'lucide-react';
import InstagramPreview from './InstagramPreview';
import InstagramStatusBadge from './InstagramStatusBadge';

type ToastFn = (message: string, type: 'success' | 'error' | 'info') => void;

export type InstagramManagementValue = {
  enabled: boolean;
  publishType: 'now' | 'scheduled';
  caption: string;
  tagsStr: string;
  useFeaturedImage: boolean;
  imageUrl: string;
  scheduledAt: string;
  status?: string;
  publishedAt?: string;
  mediaId?: string;
  permalink?: string;
  lastSyncAt?: string;
  username?: string;
};

function formatDateTime(value?: string) {
  if (!value) return 'Not available';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function InstagramManagementCard({
  featuredImage,
  value,
  onChange,
  validationErrors,
  showToast,
  allowPublishTypeChoice = false,
  publishTypeOptions,
}: {
  featuredImage: string;
  value: InstagramManagementValue;
  onChange: (next: InstagramManagementValue) => void;
  validationErrors?: Record<string, string>;
  showToast: ToastFn;
  allowPublishTypeChoice?: boolean;
  publishTypeOptions?: InstagramManagementValue['publishType'][];
}) {
  const visiblePublishTypeOptions = publishTypeOptions || ['now', 'scheduled'];
  const activeImage = value.imageUrl.trim() || (value.useFeaturedImage ? featuredImage : '');
  const update = (patch: Partial<InstagramManagementValue>) =>
    onChange({ ...value, publishType: allowPublishTypeChoice ? value.publishType : 'scheduled', ...patch });
  const showStatus = !!(value.mediaId || value.permalink || value.publishedAt || value.lastSyncAt);
  const previewCaption = [
    value.caption,
    'Read our post - {link}',
    value.tagsStr
      .split(/[,\n]/)
      .map((tag) => tag.trim())
      .filter(Boolean)
      .map((tag) => (tag.startsWith('#') ? tag : `#${tag}`))
      .join(' '),
  ]
    .filter(Boolean)
    .join('\n\n');
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    try {
      if (!allowed.includes(file.type)) {
        showToast('Only JPEG, PNG, GIF, and WebP are allowed for Instagram posting.', 'error');
        return;
      }
      const compressed = await compressImageIfNeeded(file, showToast);
      const fd = new FormData();
      fd.append('file', compressed);
      const res = await fetch('/api/upload/blogs', { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'Upload failed');
      }
      const data = (await res.json()) as { fileUrl?: string };
      if (!data.fileUrl) throw new Error('Upload did not return an image URL');
      update({ useFeaturedImage: false, imageUrl: data.fileUrl });
      showToast('Instagram image uploaded.', 'success');
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Upload failed', 'error');
    } finally {
      e.target.value = '';
    }
  };

  return (
    <section className="mt-6 pt-6 border-t border-sky-200/50">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br from-pink-500 via-rose-500 to-amber-400 text-white shadow-sm">
              <Instagram className="h-4 w-4" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 tracking-tight">Instagram Management</h3>
          </div>
          <p className="mt-1 text-sm text-gray-600">
            Configure Instagram publishing independently from Facebook.
          </p>
        </div>
        <InstagramStatusBadge status={value.status} />
      </div>

      <label className="mb-4 flex items-center justify-between rounded-lg border border-pink-100 bg-pink-50/50 px-3 py-2">
        <span className="text-sm font-semibold text-gray-800">Enable Instagram Posting</span>
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={(e) => update({ enabled: e.target.checked })}
          className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
        />
      </label>

      {value.enabled ? (
        <div className="space-y-5">
          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="text-sm font-semibold text-gray-800">Instagram Caption</label>
              <span className="text-xs text-gray-500">{value.caption.length}/2200</span>
            </div>
            <textarea
              value={value.caption}
              onChange={(e) => update({ caption: e.target.value.slice(0, 2200) })}
              rows={4}
              className={`w-full resize-none rounded-lg border-2 px-4 py-2.5 text-sm transition-all focus:border-pink-400 focus:outline-none ${
                validationErrors?.caption ? 'border-red-300 bg-red-50/50' : 'border-sky-200 bg-white hover:border-sky-300'
              }`}
              placeholder="Write a standalone Instagram caption for this article."
            />
            {validationErrors?.caption ? (
              <p className="mt-1 text-xs text-red-600">{validationErrors.caption}</p>
            ) : null}
          </div>

          <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
              <ImageIcon className="h-4 w-4 text-pink-600" />
              Instagram Image Preview
            </div>
            <label className="mb-3 flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={value.useFeaturedImage}
                onChange={(e) => update({ useFeaturedImage: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
              />
              Reuse featured image
            </label>
            <div className="mb-3 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-semibold text-pink-700 hover:text-pink-800">
                  <ImagePlus className="h-4 w-4" />
                  <span>Add Image</span>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </label>
                {value.imageUrl ? (
                  <button
                    type="button"
                    onClick={() => update({ imageUrl: '' })}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                    Remove
                  </button>
                ) : null}
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold text-gray-600">
                  External image URL
                </label>
                <input
                  type="url"
                  value={value.imageUrl}
                  onChange={(e) => update({ imageUrl: e.target.value })}
                  className="w-full rounded-lg border-2 border-sky-200 bg-white px-3 py-2 text-sm focus:border-pink-400 focus:outline-none"
                  placeholder="https://example.com/instagram-image.jpg"
                />
              </div>
            </div>
            {validationErrors?.image ? (
              <p className="mb-2 text-xs text-red-600">{validationErrors.image}</p>
            ) : null}
            <InstagramPreview
              {...(value.username ? { username: value.username } : {})}
              imageUrl={activeImage}
              caption={previewCaption || value.caption}
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-800">Tags</label>
            <input
              type="text"
              value={value.tagsStr}
              onChange={(e) => update({ tagsStr: e.target.value })}
              className="w-full rounded-lg border-2 border-sky-200 bg-white px-4 py-2.5 text-sm transition-all hover:border-sky-300 focus:border-pink-400 focus:outline-none"
              placeholder="Comma-separated (e.g. branding, thought leadership, AI)"
            />
          </div>

          {allowPublishTypeChoice && visiblePublishTypeOptions.length > 1 ? (
            <div>
              <label className="mb-2 block text-sm font-semibold text-gray-800">Instagram Publish Type</label>
              <div className="grid grid-cols-2 gap-2">
                {visiblePublishTypeOptions.map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => update({ publishType: type })}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${
                      value.publishType === type
                        ? 'border-pink-300 bg-pink-50 text-pink-800'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {type === 'now' ? 'Publish Now' : 'Schedule'}
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          {value.publishType === 'scheduled' ? (
            <div>
            <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-800">
              <CalendarClock className="h-4 w-4 text-pink-600" />
              Schedule Date & Time
            </label>
            <input
              type="datetime-local"
              value={value.scheduledAt}
              onChange={(e) => update({ scheduledAt: e.target.value })}
              className={`w-full rounded-lg border-2 px-4 py-2.5 text-sm transition-all focus:border-pink-400 focus:outline-none ${
                validationErrors?.schedule ? 'border-red-300 bg-red-50/50' : 'border-sky-200 bg-white hover:border-sky-300'
              }`}
            />
            {validationErrors?.schedule ? (
              <p className="mt-1 text-xs text-red-600">{validationErrors.schedule}</p>
            ) : null}
            </div>
          ) : null}

          {showStatus ? (
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <div className="mb-2 text-sm font-semibold text-gray-900">Instagram Status</div>
              <div className="grid gap-2 text-xs text-gray-600">
                <div>Current Status: <span className="font-semibold text-gray-900">{value.status || 'Draft'}</span></div>
                <div>Publish Date: <span className="font-semibold text-gray-900">{formatDateTime(value.publishedAt)}</span></div>
                <div>Instagram Media ID: <span className="font-mono text-gray-900">{value.mediaId || 'Not available'}</span></div>
                <div>Permalink: <span className="break-all text-gray-900">{value.permalink || 'Not available'}</span></div>
                <div>Last Sync: <span className="font-semibold text-gray-900">{formatDateTime(value.lastSyncAt)}</span></div>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
